import React from 'react';
import { Button } from './ui/button';
import { Save, FolderOpen, Download, Printer, Copy, Trash2, FileText } from 'lucide-react';
import { toPng } from 'html-to-image';
import download from 'downloadjs';
import { toast } from 'sonner';

const Toolbar = ({
  onSave,
  onLoad,
  onClear,
  onGenerateProof,
  text,
  hasUnsavedChanges,
}) => {
  const handleExportImage = async () => {
    const element = document.getElementById('monument-preview-content');
    if (!element) {
      toast.error('Preview not found');
      return;
    }

    try {
      toast.info('Generating image...');

      const images = Array.from(element.querySelectorAll('img'));
      await Promise.all(
        images.map((img) =>
          img.complete && img.naturalWidth > 0
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              })
        )
      );

      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
      });

      canvas.toBlob((blob) => {
        if (blob) {
          download(blob, `monument-inscription-${Date.now()}.png`);
          toast.success('Image exported successfully!');
        } else {
          toast.error('Failed to generate image');
        }
      });
    } catch (error) {
      console.error('Export error:', error);
      try {
        const dataUrl = await toPng(element, {
          quality: 1,
          pixelRatio: 2,
          cacheBust: true,
          backgroundColor: '#ffffff',
        });
        download(dataUrl, `monument-inscription-${Date.now()}.png`);
        toast.success('Image exported successfully!');
      } catch (fallbackError) {
        console.error('Fallback export error:', fallbackError);
        toast.error('Export failed. Please use browser screenshot or print function.');
      }
    }
  };

  const handlePrint = () => window.print();

  const handleCopy = () => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success('Text copied to clipboard!');
    } else {
      toast.error('No text to copy');
    }
  };

  // Stone-themed pill button with hard-edged corners.
  const baseBtn =
    'rounded-sm border bg-white text-stone-700 border-stone-200 hover:bg-stone-100 hover:text-stone-900 hover:border-stone-300 shadow-sm transition-all duration-200 text-sm font-medium';

  return (
    <div className="no-print bg-stone-100/60 border border-stone-200 rounded-sm p-2 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
      <div className="flex flex-wrap items-center gap-2">
        {/* Primary CTA — Generate Proof */}
        <Button
          onClick={onGenerateProof}
          disabled={!text}
          className="rounded-sm bg-stone-900 text-stone-50 hover:bg-stone-800 disabled:bg-stone-300 disabled:text-stone-500 transition-all duration-200 text-sm font-medium shadow-sm border border-stone-900"
          data-testid="generate-proof-button"
        >
          <FileText className="mr-2 h-4 w-4" />
          Funeral Home Proof
        </Button>

        <span className="hidden md:block w-px h-6 bg-stone-300 mx-1" />

        <Button onClick={onSave} variant="outline" className={baseBtn} data-testid="save-button">
          <Save className="mr-2 h-4 w-4" />
          Save
          {hasUnsavedChanges && (
            <span
              className="ml-2 w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: '#B4975A' }}
            />
          )}
        </Button>

        <Button onClick={onLoad} variant="outline" className={baseBtn} data-testid="load-button">
          <FolderOpen className="mr-2 h-4 w-4" />
          Load
        </Button>

        <Button
          onClick={handleCopy}
          variant="outline"
          className={baseBtn}
          disabled={!text}
          data-testid="copy-button"
        >
          <Copy className="mr-2 h-4 w-4" />
          Copy
        </Button>

        <Button
          onClick={handleExportImage}
          variant="outline"
          className={`${baseBtn} hover:!text-stone-900`}
          disabled={!text}
          data-testid="export-button"
          style={{ borderColor: '#B4975A', color: '#7A6633' }}
        >
          <Download className="mr-2 h-4 w-4" />
          Export Image
        </Button>

        <Button
          onClick={handlePrint}
          variant="outline"
          className={baseBtn}
          disabled={!text}
          data-testid="print-button"
        >
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>

        <span className="hidden md:block flex-1" />

        <Button
          onClick={onClear}
          variant="outline"
          className="rounded-sm border-stone-200 bg-white text-stone-500 hover:bg-red-50 hover:text-red-600 hover:border-red-300 shadow-sm transition-all duration-200 text-sm font-medium"
          data-testid="clear-button"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Clear
        </Button>
      </div>
    </div>
  );
};

export default Toolbar;
